import React, { useState, useRef, useImperativeHandle, forwardRef } from "react";
import styles from "./fix-upload.module.scss";
import { FixButton } from "@/elements-react/fix-button";
import { Icon } from "@/elements-react/fix-icon";
import classNames from "classnames";
import uploadIcon from "@/assets/upload.svg";
import loaderIcon from "@/assets/loader.svg";
import successIcon from "@/assets/upload-success.svg";
import errorIcon from "@/assets/uploading-error.svg";
import closeIcon from "@/assets/close.svg";
import { useTranslation } from "react-i18next";

type UploadType = "image" | "file";

export interface FixUploadProps {
  type?: UploadType;
  accept?: string;
  maxSize?: number;
  onUpload: (file: File) => Promise<void>;
  onReset?: () => void;
  className?: string;
  buttonText?: string;
  isOptional?: boolean;
}

export interface FixUploadRef {
  clear: () => void;
}

type UploadStatus = "idle" | "uploading" | "success" | "error";

const CONFIG: Record<UploadType, { accept: string; maxSize: number; helperText: string }> = {
  image: {
    accept: "image/png, image/jpeg",
    maxSize: 5 * 1024 * 1024,
    helperText: "JPG, PNG. Max file size: 5 MB",
  },
  file: {
    accept: ".xlsx, .csv, .txt, text/csv, text/plain",
    maxSize: 2 * 1024 * 1024,
    helperText: "XLSX, CSV, TXT. Max file size: 2 MB",
  },
};

export const FixUpload = forwardRef<FixUploadRef, FixUploadProps>(({
  type = "image",
  accept,
  onUpload,
  onReset,
  className,
  buttonText = "Choose photo",
  isOptional = false,
}, ref) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<UploadStatus>("idle");
  const [fileName, setFileName] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const config = CONFIG[type];
  const activeAccept = accept ?? config.accept;
  const helperText = config.helperText;

  useImperativeHandle(ref, () => ({
    clear: () => {
      handleReset();
    }
  }));

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setStatus("uploading");

    try {
      await onUpload(file);
      setStatus("success");
    } catch (error) {
      console.error("Upload failed", error);
      setStatus("error");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRetry = () => {
    fileInputRef.current?.click();
  };

  const handleReset = () => {
    setStatus("idle");
    setFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    onReset?.();
  };

  return (
    <div className={classNames(styles.uploadContainer, className)}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={activeAccept}
        className={styles.hiddenInput}
      />

      {status === "idle" && (
        <div className={styles.idleState}>
          <FixButton
            variant="default"
            className={styles['upload-button']}
            onClick={() => fileInputRef.current?.click()}
            prefix={<Icon src={uploadIcon as string} width={20} height={20} />}
          >
            {buttonText}
          </FixButton>
          <div className={styles.helperWrap}>
            <span className={styles.helperText}>{helperText}</span>
            {isOptional && <span className={styles.optionalText}>{t('file.upload.optional.text', '(Optional)')}</span>}
          </div>
        </div>
      )}

      {status === "uploading" && (
        <div className={classNames(styles.stateContainer, styles.uploading)}>
          <div className={styles.uploadingContent}>
            <Icon src={loaderIcon as string} width={24} height={24} />
            <span>{t('file.upload.status.uploading', 'Uploading file...')}</span>
          </div>
        </div>
      )}

      {status === "success" && (
        <div className={classNames(styles.stateContainer, styles.success)}>
          <div className={styles.successContent}>
            <Icon src={successIcon as string} width={24} height={24} />
            <span className={styles.fileName}>{fileName}</span>
          </div>
          <button onClick={handleReset} className={styles.closeButton} aria-label="Remove file">
            <Icon src={closeIcon as string} width={20} height={20} />
          </button>
        </div>
      )}

      {status === "error" && (
        <div className={classNames(styles.stateContainer, styles.error)}>
          <div className={styles.errorContent}>
            <Icon src={errorIcon as string} width={24} height={24} />
            <div className={styles.errorMessage}>
              <span className={styles.errorTitle}>{t('file.upload.status.error.title', 'Upload failed')}</span>
              <span className={styles.errorDescription}>{t('file.upload.status.error.description', 'Failed to upload file. Try again.')}</span>
            </div>
          </div>
          <button onClick={handleRetry} className={styles.tryAgainButton}>
            {t('file.upload.status.error.try', 'Try again')}
          </button>
        </div>
      )}
    </div>
  );
});
